/**
 * Default Bound Profiles — one per call category (10).
 *
 * A bound profile is auto-selected by the analyze pipeline via
 * prompt_profiles.binding (org-level). Toggles are baked per category — there
 * is no runtime override layer on top of them (WYSIWYG: what the profile
 * stores is what runs). The stored systemPrompt is generated with the
 * category-specific role/rubrics so UI preview matches runtime behavior.
 *
 * Plan: docs/plans/2026-06-10-category-prompt-profiles.md
 */

import type { PromptProfile, PromptToggles } from '@aica/core';
import { XML_USER_TEMPLATE } from './xml-templates';
import { CALL_CATEGORIES, type CallCategory } from './call-categories';
import {
  DEFAULT_SALES_TOGGLES,
  DEFAULT_SUPPORT_TOGGLES,
  DEFAULT_CHAT_TOGGLES,
  DEFAULT_COMPLAINT_TOGGLES,
} from './profiles';

type BaseCategory = 'sales-call' | 'support-call' | 'sales-chat' | 'complaint';

/** Auto-selection key: the detected call category, for calls and chats alike. */
export type ProfileBinding = CallCategory;

export const PROFILE_BINDINGS: readonly ProfileBinding[] = CALL_CATEGORIES;

export interface BoundProfileSpec {
  base: BaseCategory;
  name: string;
  description: string;
  /** Per-category toggle deltas applied over the base category defaults. */
  toggleDeltas: Partial<PromptToggles>;
}

const BASE_TOGGLES: Record<BaseCategory, PromptToggles> = {
  'sales-call': DEFAULT_SALES_TOGGLES,
  'support-call': DEFAULT_SUPPORT_TOGGLES,
  'sales-chat': DEFAULT_CHAT_TOGGLES,
  complaint: DEFAULT_COMPLAINT_TOGGLES,
};

/**
 * Canonical (English) specs for the default bound profiles. Exported so the
 * UI can recognize an untouched default by its stored name/description and
 * render a localized label instead (renamed profiles display as-is).
 */
export const BOUND_PROFILE_SPECS: Record<ProfileBinding, BoundProfileSpec> = {
  product_inquiry: {
    base: 'sales-call',
    name: 'Product Inquiry',
    description:
      'Discovery quality, needs qualification and information completeness for product consultations',
    toggleDeltas: {},
  },
  purchase: {
    base: 'sales-call',
    name: 'Purchase',
    description:
      'Closing quality, objection handling and conversion probability for purchase-intent calls',
    toggleDeltas: {},
  },
  scheduling: {
    base: 'sales-call',
    name: 'Scheduling',
    description:
      'Booking conversion, appointment clarity and meeting preparation for scheduling calls',
    toggleDeltas: { objectionHandling: false },
  },
  follow_up: {
    base: 'sales-call',
    name: 'Follow-up',
    description:
      'Context continuity, commitment follow-through and deal progression for follow-up calls',
    toggleDeltas: {},
  },
  support: {
    base: 'support-call',
    name: 'Support Requests',
    description:
      'Issue resolution quality, troubleshooting accuracy and empathy for support calls',
    toggleDeltas: { objectionHandling: true },
  },
  billing: {
    base: 'support-call',
    name: 'Billing',
    description:
      'Accuracy, dispute de-escalation and clarity of explanation for billing inquiries',
    toggleDeltas: { objectionHandling: true },
  },
  onboarding: {
    base: 'support-call',
    name: 'Onboarding',
    description:
      'Completeness of explanation, next steps and expectation setting for onboarding sessions',
    toggleDeltas: {},
  },
  complaint: {
    base: 'complaint',
    name: 'Complaints',
    description:
      'De-escalation, empathy, resolution quality and retention signals for complaint calls',
    toggleDeltas: {},
  },
  cancellation: {
    base: 'complaint',
    name: 'Cancellation & Retention',
    description:
      'Retention attempt quality, churn reason capture and save-offer handling for cancellations',
    toggleDeltas: {},
  },
  other: {
    base: 'sales-call',
    name: 'Other Conversations',
    description:
      'General communication quality for personal, off-topic or unclassified conversations',
    // Personal / internal / non-customer conversations: emit only a descriptive record
    // (summary, topic, keywords, key_moments, sentiment) plus the safety signal
    // (attention_flag). Profanity is not a toggle: the built-in criterion
    // `agent_profanity` covers every category. Suppress ALL agent-coaching/QA and sales/BI
    // fields — there is no agent-to-customer interaction to grade, so scoring greeting,
    // operator errors, politeness, etc. only fabricates findings on a private call.
    toggleDeltas: {
      qualityScore: false,
      saleProbability: false,
      greetingIntro: false,
      needsQualification: false,
      objectionHandling: false,
      closingTechniques: false,
      operatorErrors: false,
      politeness: false,
      clarity: false,
      interruptions: false,
      fillerWords: false,
      stopWords: false,
      crmCompleteness: false,
      strengthsImprovements: false,
      businessIntelligence: false,
    },
  },
};

/**
 * Build ONE default bound profile.
 *
 * `systemPrompt` is deliberately empty. Nothing in the analysis path reads a
 * stored system prompt: `PromptBuilder.buildPrompt` regenerates it from
 * `toggles` on every call (builder.ts), the analyze worker maps the stored
 * column to `''` before it ever gets there
 * (workers/analyze/prompt-profile.ts), and the admin preview copies the
 * stored string into a profile object whose `systemPrompt` the builder then
 * ignores (routes/prompt-profiles/preview.ts). Filling it cost 587 KB of XML
 * per organization — ~59 KB per bound profile, each inlining the few-shot
 * example constants — built during provisioning and written into D1, then
 * mirrored to Postgres, for nobody.
 *
 * To see what a profile's system prompt actually looks like, build it:
 * `buildXMLSystemPrompt({ category, toggles, callCategory, channel, includeExamples })`
 * — that is what production does anyway, which is the whole point.
 */
function createBoundProfile(
  orgId: string,
  binding: ProfileBinding,
  now: string
): PromptProfile {
  const spec = BOUND_PROFILE_SPECS[binding];
  const toggles: PromptToggles = { ...BASE_TOGGLES[spec.base], ...spec.toggleDeltas };

  return {
    id: `${orgId}-binding-${binding}`,
    orgId,
    name: spec.name,
    description: spec.description,
    category: spec.base,
    binding,
    toggles,
    systemPrompt: '',
    userTemplate: XML_USER_TEMPLATE,
    version: 1,
    createdAt: now,
  };
}

/**
 * Create the default bound profiles for an organization (10 profiles, one
 * per call category). Complements createDefaultProfiles() — the 4 unbound
 * base profiles remain as project-level fallbacks.
 *
 * In-memory ids are deterministic (`{orgId}-binding-{binding}`); provisioning
 * code replaces them with UUIDs on insert.
 */
export function createDefaultBoundProfiles(orgId: string): PromptProfile[] {
  const now = new Date().toISOString();
  return PROFILE_BINDINGS.map((binding) => createBoundProfile(orgId, binding, now));
}

/**
 * Get the default profile spec for a single binding (used by restore-default).
 *
 * Builds only the binding asked for. It used to build all ten and `.find()`
 * one, discarding nine — on a path a user triggers by clicking "restore
 * default" on a single profile.
 */
export function createDefaultBoundProfile(
  orgId: string,
  binding: ProfileBinding
): PromptProfile {
  if (!(binding in BOUND_PROFILE_SPECS)) {
    throw new Error(`Unknown profile binding: ${binding}`);
  }
  return createBoundProfile(orgId, binding, new Date().toISOString());
}
